/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { DashboardCloudHttpService } from "./http/dashboard-cloud-http.service";
import { Observable } from "rxjs";
import { ChartResponse } from "../models/ui/charts.model";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import { KPI } from "../models/http/dashboard/kpi.model";
import { CloudScopeMode } from "../models/ui/cloud-scope-mode.model";
import { CloudScopeOrderBy } from "../models/ui/cloud-scope-orderby.model";
import { CloudFiltersResponse } from "../models/http/dashboard/cloud/cloud-filters-model";
import { PieChart } from "../models/http/charts/pie-chart/pie-chart.model";
import { ReportParams } from "../models/http/dashboard/report-params.model";

@Injectable({
	providedIn: "root"
})
export class DashboardCloudService {
	constructor(private _dashboardCloudHttpService: DashboardCloudHttpService) {}

	public getCloudFilters(tenantId: string): Observable<CloudFiltersResponse> {
		return this._dashboardCloudHttpService.getCloudFilters(tenantId);
	}

	public getCloudPerformance(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<LineChart>> {
		return this._dashboardCloudHttpService.getCloudPerformance(
			tenantId,
			cloudProviders,
			applications,
			locations,
			startDate,
			endDate
		);
	}

	public getCloudEmissionsForPieChart(
		tenantId: string,
		startDate: string,
		endDate: string,
		cloudProviders: string[],
		locations: string[],
		applications: string[]
	): Observable<ChartResponse<PieChart>> {
		return this._dashboardCloudHttpService.getCloudEmissionsForPieChart(
			tenantId,
			startDate,
			endDate,
			cloudProviders,
			locations,
			applications
		);
	}

	public getTotalCo2(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<KPI> {
		return this._dashboardCloudHttpService.getTotalCo2(
			tenantId,
			cloudProviders,
			applications,
			locations,
			startDate,
			endDate
		);
	}

	public getWorst10Applications(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardCloudHttpService.getWorst10Applications(
			tenantId,
			cloudProviders,
			applications,
			locations,
			startDate,
			endDate
		);
	}

	public getWorst10Locations(
		tenantId: string,
		cloudProviders: string[],
		applications: string[],
		locations: string[],
		startDate: string,
		endDate: string
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardCloudHttpService.getWorst10Locations(
			tenantId,
			cloudProviders,
			applications,
			locations,
			startDate,
			endDate
		);
	}

	public getEmissionsAnalysis(
		tenantId: string,
		startDate: string,
		endDate: string,
		locations: string[],
		applications: string[],
		mode: CloudScopeMode,
		orderBy: CloudScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardCloudHttpService.getEmissionsAnalysis(
			tenantId,
			startDate,
			endDate,
			locations,
			applications,
			mode,
			orderBy
		);
	}

	public getRegionsEmissions(
		tenantId: string,
		startDate: string,
		endDate: string,
		cloudProviders: string[],
		locations: string[],
		applications: string[],
		mode: CloudScopeMode,
		orderBy: CloudScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardCloudHttpService.getRegionsEmissions(
			tenantId,
			startDate,
			endDate,
			cloudProviders,
			locations,
			applications,
			mode,
			orderBy
		);
	}

	public exportResponseInCsv(
		startDate: string,
		endDate: string,
		cloudProvider: string[],
		applicationId: string[],
		locationId: string[],
		tenantId: string
	) {
		return this._dashboardCloudHttpService.exportResponseInCsv(
			startDate,
			endDate,
			cloudProvider,
			locationId,
			applicationId,
			tenantId
		);
	}

	public exportReportInPDF(reportParams: ReportParams, tenantId: string) {
		return this._dashboardCloudHttpService.exportReport(reportParams, tenantId, "pdf");
	}
}
